import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  FormLayout,
  Select,
  TextField,
  Button,
  Banner,
  Layout,
  BlockStack,
  InlineStack,
  Checkbox,
  Text,
} from '@shopify/polaris';
import { useConfig } from '../contexts/ConfigContext';
import Loading from '../components/Loading';

const Configuration: React.FC = () => {
  const { config, isLoading, updateConfig, refreshConfig } = useConfig();
  const [localConfig, setLocalConfig] = useState(config);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  if (isLoading || !localConfig) {
    return <Loading message="Loading configuration..." />;
  }

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);
      await updateConfig(localConfig);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      await refreshConfig();
      setLocalConfig(config);
    } catch (err) {
      setSaveError('Failed to reset configuration');
    }
  };

  return (
    <Page
      title="Configuration"
      subtitle="Configure your Tally export settings"
    >
      <Layout>
        {saveSuccess && (
          <Layout.Section>
            <Banner
              title="Configuration saved"
              tone="success"
              onDismiss={() => setSaveSuccess(false)}
            />
          </Layout.Section>
        )}

        {saveError && (
          <Layout.Section>
            <Banner
              title="Error"
              tone="critical"
              onDismiss={() => setSaveError(null)}
            >
              <p>{saveError}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Tally Settings</Text>
              <FormLayout>
                <FormLayout.Group>
                  <Select
                    label="Tally Version"
                    options={[
                      { label: 'TallyPrime', value: 'prime' },
                      { label: 'Tally.ERP 9', value: 'erp9' },
                    ]}
                    value={localConfig.tallyVersion}
                    onChange={(value) =>
                      setLocalConfig({ ...localConfig, tallyVersion: value as 'prime' | 'erp9' })
                    }
                  />
                  <Select
                    label="Default Export Format"
                    options={[
                      { label: 'XML', value: 'xml' },
                      { label: 'Excel', value: 'excel' },
                      { label: 'Both', value: 'both' },
                    ]}
                    value={localConfig.defaultFormat}
                    onChange={(value) =>
                      setLocalConfig({ ...localConfig, defaultFormat: value as 'xml' | 'excel' | 'both' })
                    }
                  />
                </FormLayout.Group>

                <TextField
                  label="Company Name"
                  value={localConfig.companyName}
                  onChange={(value) => setLocalConfig({ ...localConfig, companyName: value })}
                  autoComplete="off"
                />

                <Select
                  label="Date Format"
                  options={[
                    { label: 'DD-MM-YYYY', value: 'dd-MM-yyyy' },
                    { label: 'MM/DD/YYYY', value: 'MM/dd/yyyy' },
                    { label: 'YYYY-MM-DD', value: 'yyyy-MM-dd' },
                  ]}
                  value={localConfig.dateFormat}
                  onChange={(value) => setLocalConfig({ ...localConfig, dateFormat: value })}
                />
              </FormLayout>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Field Mapping</Text>
              <FormLayout>
                <FormLayout.Group>
                  <TextField
                    label="Sales Ledger"
                    value={localConfig.fieldMapping.salesLedger}
                    onChange={(value) =>
                      setLocalConfig({
                        ...localConfig,
                        fieldMapping: { ...localConfig.fieldMapping, salesLedger: value },
                      })
                    }
                    autoComplete="off"
                  />
                  <TextField
                    label="Shipping Ledger"
                    value={localConfig.fieldMapping.shippingLedger}
                    onChange={(value) =>
                      setLocalConfig({
                        ...localConfig,
                        fieldMapping: { ...localConfig.fieldMapping, shippingLedger: value },
                      })
                    }
                    autoComplete="off"
                  />
                </FormLayout.Group>

                <FormLayout.Group>
                  <TextField
                    label="Tax Ledger"
                    value={localConfig.fieldMapping.taxLedger}
                    onChange={(value) =>
                      setLocalConfig({
                        ...localConfig,
                        fieldMapping: { ...localConfig.fieldMapping, taxLedger: value },
                      })
                    }
                    autoComplete="off"
                  />
                  <TextField
                    label="Discount Ledger"
                    value={localConfig.fieldMapping.discountLedger}
                    onChange={(value) =>
                      setLocalConfig({
                        ...localConfig,
                        fieldMapping: { ...localConfig.fieldMapping, discountLedger: value },
                      })
                    }
                    autoComplete="off"
                  />
                </FormLayout.Group>

                <FormLayout.Group>
                  <TextField
                    label="Customer Prefix"
                    value={localConfig.fieldMapping.customerPrefix}
                    onChange={(value) =>
                      setLocalConfig({
                        ...localConfig,
                        fieldMapping: { ...localConfig.fieldMapping, customerPrefix: value },
                      })
                    }
                    autoComplete="off"
                  />
                  <TextField
                    label="Product Prefix"
                    value={localConfig.fieldMapping.productPrefix}
                    onChange={(value) =>
                      setLocalConfig({
                        ...localConfig,
                        fieldMapping: { ...localConfig.fieldMapping, productPrefix: value },
                      })
                    }
                    autoComplete="off"
                  />
                </FormLayout.Group>
              </FormLayout>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Default Export Options</Text>
              <BlockStack gap="200">
                <Checkbox
                  label="Include shipping charges by default"
                  checked={localConfig.filters.includeShipping}
                  onChange={(value) =>
                    setLocalConfig({
                      ...localConfig,
                      filters: { ...localConfig.filters, includeShipping: value },
                    })
                  }
                />
                <Checkbox
                  label="Include taxes by default"
                  checked={localConfig.filters.includeTax}
                  onChange={(value) =>
                    setLocalConfig({
                      ...localConfig,
                      filters: { ...localConfig.filters, includeTax: value },
                    })
                  }
                />
                <Checkbox
                  label="Include discounts by default"
                  checked={localConfig.filters.includeDiscounts}
                  onChange={(value) =>
                    setLocalConfig({
                      ...localConfig,
                      filters: { ...localConfig.filters, includeDiscounts: value },
                    })
                  }
                />
                <Checkbox
                  label="Group exports by date"
                  checked={localConfig.filters.groupByDate}
                  onChange={(value) =>
                    setLocalConfig({
                      ...localConfig,
                      filters: { ...localConfig.filters, groupByDate: value },
                    })
                  }
                />
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <InlineStack align="end" gap="200">
            <Button onClick={handleReset} disabled={isSaving}>
              Reset to Defaults
            </Button>
            <Button variant="primary" onClick={handleSave} loading={isSaving}>
              Save Configuration
            </Button>
          </InlineStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default Configuration;
